import React, { useCallback, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { UNSAFE_NavigationContext as NavigationContext } from "react-router-dom";
import type { Navigator } from "react-router-dom";

/**
 * Hook to prompt the user when navigating away with unsaved changes (isDirty)
 * Usage:
 *   const [showPrompt, confirmLeave, cancelLeave] = useUnsavedChangesWarning(isDirty);
 */
type UnsavedChangesWarningResult = {
  showModal: boolean;
  confirmLeave: () => void;
  cancelLeave: () => void;
  confirmContent: { title: string; content: string };
  unlock: () => void;
};

export function useUnsavedChangesWarning(isDirty: boolean): UnsavedChangesWarningResult {
  const { navigator } = React.useContext(NavigationContext) as { navigator: Navigator };
  const unblockRef = useRef<null | (() => void)>(null);
  const txRef = useRef<any>(null);
  const isDirtyRef = useRef(isDirty);
  const [showModal, setShowModal] = React.useState(false);
  const { t } = useTranslation()

  // Nội dung confirm có thể custom thêm nếu muốn
  const confirmContent = {
    title: t("common.confirmOut"),
    content: t("common.confirmOutContent")
  };

  useEffect(() => {
    isDirtyRef.current = isDirty;
  }, [isDirty]);

  const confirmLeave = useCallback(() => {
    setShowModal(false);
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
    if (txRef.current) {
      txRef.current.retry();
      txRef.current = null;
    }
  }, []);

  const cancelLeave = useCallback(() => {
    setShowModal(false);
    txRef.current = null;
  }, []);

  const unlock = useCallback(() => {
    if (unblockRef.current) {
      unblockRef.current();
      unblockRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!isDirty) {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      return;
    }
    // Block navigation
    unblockRef.current = (navigator as any).block((tx: any) => {
      if (tx.location && tx.location.pathname === "/login") {
        if (unblockRef.current) {
          unblockRef.current();
          unblockRef.current = null;
        }
        tx.retry();
        return;
      }
      setShowModal(true);
      txRef.current = tx;
    });
    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty, navigator]);

  // Warn if user reloads/tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirtyRef.current) {
        e.preventDefault();
        e.returnValue = confirmContent.content;
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, []);

  return { showModal, confirmLeave, cancelLeave, confirmContent, unlock };
}

